/**
 * usePermissions Hook - Role-based access control
 * 
 * Fetches user profile from backend and provides permission checking methods
 * Integrates with OPA for authorization decisions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';

/**
 * User profile structure from backend
 */
const DEFAULT_PROFILE = {
  roles: [],
  projects: [],
  agents: [],
  isProfiled: false,
  isPlatformAdmin: false,
  isProjectAdmin: false
};

/**
 * Permissions hook providing role-based access control
 */
export const usePermissions = () => {
  const { isAuthenticated, getAccessToken } = useAuth();
  
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUnprofiled, setIsUnprofiled] = useState(false);
  
  // Use refs to track if we've already loaded the profile to prevent infinite loops
  const profileLoadedRef = useRef(false);
  const lastAuthStateRef = useRef(isAuthenticated);
  const abortControllerRef = useRef(null);

  // Fetch profile when authentication state changes
  useEffect(() => {
    // If not authenticated, reset state if auth state changed
    if (!isAuthenticated) {
      if (lastAuthStateRef.current !== isAuthenticated) {
        console.log('Not authenticated, resetting state');
        setProfile(DEFAULT_PROFILE);
        setIsUnprofiled(false);
        setIsLoading(false);
        setError(null);
        profileLoadedRef.current = false;
        lastAuthStateRef.current = isAuthenticated;
      }
      return;
    }

    // If already loaded and still authenticated, don't refetch
    if (profileLoadedRef.current && isAuthenticated) {
      console.log('Profile already loaded, skipping fetch');
      lastAuthStateRef.current = isAuthenticated;
      return;
    }

    console.log('Fetching profile - isAuthenticated:', isAuthenticated, 'profileLoaded:', profileLoadedRef.current);

    const fetchProfile = async () => {
      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('usePermissions: Starting profile fetch...');
        
        // Get access token
        const token = await getAccessToken();
        console.log('usePermissions: Got token:', !!token);
        console.log('usePermissions: Token preview:', token ? `${token.substring(0, 50)}...` : 'null');
        
        if (!token) {
          console.error('usePermissions: No access token available from getAccessToken()');
          // Set a special error that will trigger re-authentication
          setError('Authentication required');
          setIsLoading(false);
          profileLoadedRef.current = false;
          return;
        }
        
        // Make API request with abort signal
        // Use environment variable or fallback to relative path for production
        const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '');
        // Remove duplicate /api if already present in base URL
        const cleanBaseUrl = apiBaseUrl.endsWith('/api') ? apiBaseUrl : `${apiBaseUrl}/api`;
        const url = `${cleanBaseUrl}/auth/profile`;
        console.log('usePermissions: Making request to:', url);
        console.log('usePermissions: Request headers will include Authorization Bearer token');
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          signal: abortControllerRef.current.signal
        });

        console.log('usePermissions: Response received:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: Object.fromEntries(response.headers.entries())
        });

        if (response.status === 401 && response.headers.get('X-Reason') === 'unprofiled') {
          console.log('usePermissions: User is unprofiled (401 with X-Reason: unprofiled)');
          setIsUnprofiled(true);
          setProfile(DEFAULT_PROFILE);
          setIsLoading(false);
          profileLoadedRef.current = false;
          return;
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.error('usePermissions: API request failed:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText
          });
          
          // Check if this is a new user (401 without unprofiled header)
          if (response.status === 401) {
            console.log('usePermissions: 401 error - likely new user awaiting approval');
            setIsUnprofiled(true);
            setProfile(DEFAULT_PROFILE);
            setIsLoading(false);
            profileLoadedRef.current = false;
            return;
          }
          
          throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
        }

        const profileData = await response.json();
        console.log('usePermissions: Profile data received:', profileData);
        
        // Transform backend response to frontend format
        const transformedProfile = {
          roles: profileData.roles || [],
          projects: profileData.projects || [],
          agents: profileData.agents || [],
          isProfiled: true,
          isPlatformAdmin: profileData.globalRoles?.includes('platform_admin') || false,
          isProjectAdmin: profileData.globalRoles?.includes('project_admin') || profileData.roles?.includes('project_admin') || false,
          globalRoles: profileData.globalRoles || [],
          projectRoles: profileData.projectRoles || [],
          email: profileData.email,
          displayName: profileData.displayName,
          tenantId: profileData.tenantId
        };
        
        console.log('Transformed profile:', transformedProfile);
        setProfile(transformedProfile);
        setIsUnprofiled(false);
        setIsLoading(false);
        profileLoadedRef.current = true;
        lastAuthStateRef.current = isAuthenticated;
        
      } catch (err) {
        // Ignore aborted requests
        if (err.name === 'AbortError') {
          console.log('Profile fetch aborted');
          return;
        }
        
        console.error('Failed to fetch user profile:', err);
        setError(err.message);
        setProfile(DEFAULT_PROFILE);
        setIsLoading(false);
        profileLoadedRef.current = false;
        lastAuthStateRef.current = isAuthenticated;
      }
    };

    fetchProfile();

    // Cleanup function to cancel ongoing requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isAuthenticated, getAccessToken]); // Added getAccessToken back but with better logic

  /**
   * Refresh profile data
   */
  const refreshProfile = useCallback(() => {
    profileLoadedRef.current = false;
    setIsLoading(true);
    // Trigger re-fetch by changing auth state reference
    lastAuthStateRef.current = !lastAuthStateRef.current;
  }, []);

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback((role) => {
    return profile.roles.includes(role) || profile.globalRoles?.includes(role);
  }, [profile.roles, profile.globalRoles]);

  /**
   * Check if user has access to specific project
   */
  const hasProjectAccess = useCallback((projectId) => {
    if (profile.isPlatformAdmin) {
      return true;
    }
    
    return profile.projects.some(p => p.id === projectId || p.projectId === projectId);
  }, [profile.isPlatformAdmin, profile.projects]);

  /**
   * Check if user has specific role in project
   */
  const hasProjectRole = useCallback((projectId, role) => {
    if (profile.isPlatformAdmin) {
      return true;
    }
    
    const projectRole = profile.projectRoles?.find(pr => pr.projectId === projectId);
    return projectRole?.roles?.includes(role) || false;
  }, [profile.isPlatformAdmin, profile.projectRoles]);

  /**
   * Check if user can access admin panel
   */
  const canAccessAdmin = useCallback(() => {
    // If profile is not loaded yet (globalRoles is undefined), return false but don't block
    // This prevents premature permission denials during profile loading
    if (!profile.globalRoles && !profile.roles) {
      return false;
    }
    
    return profile.isPlatformAdmin || hasRole('platform_admin');
  }, [profile.isPlatformAdmin, profile.globalRoles, profile.roles, hasRole]);

  /**
   * Check if user can access project settings
   */
  const canAccessProjectSettings = useCallback(() => {
    return profile.isPlatformAdmin || profile.isProjectAdmin || hasRole('project_admin');
  }, [profile.isPlatformAdmin, profile.isProjectAdmin, hasRole]);

  /**
   * Check if user can access settings (alias for canAccessProjectSettings)
   */
  const canAccessSettings = useCallback(() => {
    return canAccessProjectSettings();
  }, [canAccessProjectSettings]);

  /**
   * Check if user can access MCP servers
   */
  const canAccessMCP = useCallback(() => {
    // Only platform admins can access MCP servers
    return profile.isPlatformAdmin || hasRole('platform_admin');
  }, [profile.isPlatformAdmin, hasRole]);

  /**
   * Check if user is agent admin for a specific project
   */
  const isAgentAdmin = useCallback((projectId) => {
    if (profile.isPlatformAdmin) return true;
    
    const projectRole = profile.projectRoles?.find(pr => pr.projectId === projectId);
    return projectRole?.roles?.includes('agent_admin') || false;
  }, [profile.isPlatformAdmin, profile.projectRoles]);

  /**
   * Check if user is super user for a specific project
   */
  const isSuperUser = useCallback((projectId) => {
    if (profile.isPlatformAdmin) return true;
    
    const projectRole = profile.projectRoles?.find(pr => pr.projectId === projectId);
    return projectRole?.roles?.includes('super_user') || false;
  }, [profile.isPlatformAdmin, profile.projectRoles]);

  /**
   * Get user's role hierarchy level (higher number = more permissions)
   */
  const getUserRoleLevel = useCallback(() => {
    if (profile.isPlatformAdmin) return 5; // Platform Admin
    if (profile.isProjectAdmin) return 4;  // Project Admin
    if (profile.globalRoles?.includes('agent_admin')) return 3; // Agent Admin
    if (profile.globalRoles?.includes('super_user')) return 2;  // Super User
    return 1; // Regular User
  }, [profile.isPlatformAdmin, profile.isProjectAdmin, profile.globalRoles]);

  /**
   * Get user's accessible projects
   */
  const getAccessibleProjects = useCallback(() => {
    return profile.projects || [];
  }, [profile.projects]);

  /**
   * Get user's accessible agents
   */
  const getAccessibleAgents = useCallback(() => {
    return profile.agents || [];
  }, [profile.agents]);

  /**
   * Check if user has any roles
   */
  const hasAnyRole = useCallback(() => {
    return (profile.roles && profile.roles.length > 0) || 
           (profile.globalRoles && profile.globalRoles.length > 0);
  }, [profile.roles, profile.globalRoles]);

  /**
   * Get default route for user based on roles
   */
  const getDefaultRoute = useCallback(() => {
    if (isUnprofiled) {
      return '/awaiting-approval';
    }
    
    // Always default to chat to avoid permission issues during navigation
    // Users can manually navigate to admin panel if needed
    return '/chat';
  }, [isUnprofiled]);

  return {
    // Profile data
    profile,
    isLoading,
    error,
    isUnprofiled,
    
    // Permission checking methods
    hasRole,
    hasProjectAccess,
    hasProjectRole,
    canAccessAdmin,
    canAccessProjectSettings,
    canAccessSettings,
    canAccessMCP,
    isAgentAdmin,
    isSuperUser,
    getUserRoleLevel,
    hasAnyRole,
    
    // Data access methods
    getAccessibleProjects,
    getAccessibleAgents,
    
    // Route helpers
    getDefaultRoute,
    
    // Utility methods
    refreshProfile
  };
}; 